<template>
  <div class="page-wrapper">
    <RouterLink
      class="confirmation-page__back-link"
      :to="{ name: 'MyBookings' }"
    >
      <svg class="confirmation-page__back-link-icon">
        <use :xlink:href="`#${chevron}`" />
      </svg>
      {{ t('confirmation.backToTickets') }}
    </RouterLink>
    <section class="confirmation-page">
      <div class="confirmation-page__main card">
        <h1 class="headline-medium">{{ t('confirmation.thankYou') }}</h1>
        <p class="title-medium">
          {{ t('confirmation.bookingNumber') }}: {{ bookingNumber }}
        </p>
        <p v-if="customerEmail">
          {{ t('confirmation.receiptEmail') }}: {{ customerEmail }}
        </p>
        <p v-else>{{ t('confirmation.loadingEmail') }}</p>
      </div>
      <section class="confirmation-page__sidebar card">
        <TicketCard
          v-if="outboundTravelInfo && bookingNumber"
          :bookingNumber="bookingNumber"
          :departureDate="formatDate(outboundTravelInfo.departureDateTime)"
          :departureTime="formatTime(outboundTravelInfo.departureDateTime)"
          :arrivalTime="formatTime(outboundTravelInfo.arrivalDateTime)"
          :originStation="outboundTravelInfo.originStation.name"
          :destinationStation="outboundTravelInfo.destinationStation.name"
          :segments="outboundTravelInfo.segments || []"
        />
        <TicketCard
          v-if="returnTravelInfo && bookingNumber"
          :bookingNumber="bookingNumber"
          :departureDate="formatDate(returnTravelInfo.departureDateTime)"
          :departureTime="formatTime(returnTravelInfo.departureDateTime)"
          :arrivalTime="formatTime(returnTravelInfo.arrivalDateTime)"
          :originStation="returnTravelInfo.originStation.name"
          :destinationStation="returnTravelInfo.destinationStation.name"
          :segments="returnTravelInfo.segments || []"
        />
        <p v-if="!outboundTravelInfo && !returnTravelInfo">
          {{ t('confirmation.loadingDetails') }}
        </p>
      </section>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent, computed, onMounted } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useUserStore } from '@/stores/userStore'
  import { useErrorStore } from '@/stores/errorStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import { fetchBookingData } from '@/services/journeyService'
  import TicketCard from '@/components/MyPage/TicketCard.vue'
  import chevron from '@/assets/icons/chevron.svg'

  export default defineComponent({
    name: 'ConfirmationPage',
    components: {
      TicketCard,
    },
    setup() {
      const { t } = useI18n()
      const route = useRoute()
      const bookingStore = useBookingStore()
      const userStore = useUserStore()
      const errorStore = useErrorStore()
      const authStore = useAuthStore()

      const bookingNumber = computed(() => {
        const routeParam = route.params.bookingNumber
        const paramValue = Array.isArray(routeParam) ? routeParam[0] : routeParam
        return paramValue || bookingStore.bookingNumber || ''
      })
      const customerEmail = computed(
        () => bookingStore.bookingData?.customer?.email || ''
      )
      const bookingData = computed(() => bookingStore.bookingData)

      const getTravelInfo = (travel: any) => {
        if (!travel) return null
        const route = travel.routes[0]
        const firstLeg = route.legs[0]
        const lastLeg = route.legs[route.legs.length - 1]

        return {
          departureDateTime: firstLeg.departure_timestamp,
          arrivalDateTime: lastLeg.arrival_timestamp,
          originStation: firstLeg.departure_station,
          destinationStation: lastLeg.arrival_station,
          segments: route.legs || [],
        }
      }

      const outboundTravelInfo = computed(() =>
        getTravelInfo(bookingStore.outboundTravel)
      )
      const returnTravelInfo = computed(() =>
        getTravelInfo(bookingStore.inboundTravel)
      )

      const formatDate = (dateString?: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('no', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(date)
      }

      const formatTime = (dateString?: string) => {
        if (!dateString) return ''
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('no', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(date)
      }

      onMounted(async () => {
        if (!bookingNumber.value) {
          console.error('No booking number found')
          errorStore.setError(t('confirmation.noBooking'))
          return
        }

        if (!userStore.isLoggedIn) {
          const pendingPayment = localStorage.getItem('pendingPayment')
          if (pendingPayment) {
            try {
              const paymentData = JSON.parse(pendingPayment)
              if (paymentData.bookingToken && paymentData.bookingNumber === bookingNumber.value) {
                const tokenAge = Date.now() - (paymentData.timestamp || 0)
                if (tokenAge < 30 * 60 * 1000) { 
                  const expirationTime = Date.now() + 20 * 60 * 1000 
                  authStore.setToken(
                    paymentData.bookingToken, 
                    expirationTime, 
                    'booking', 
                    bookingNumber.value
                  )
                  console.log('Restored booking token from localStorage for guest user')
                }
              }
            } catch (error) {
              console.error('Error restoring booking token:', error)
            }
          }
        }

        if (bookingStore.outboundTravel || bookingStore.inboundTravel) {
          console.log('Using existing booking data from store:', {
            outboundTravel: !!bookingStore.outboundTravel,
            inboundTravel: !!bookingStore.inboundTravel,
            customerEmail: bookingStore.bookingData?.customer?.email,
          })
        } else {
          try {
            let token: string | undefined = undefined;
            
            if (userStore.isLoggedIn && userStore.token !== null) {
              token = userStore.token;
            } else if (authStore.isBookingToken && authStore.token !== null) {
              token = authStore.token;
            } else if (typeof bookingStore.bookingToken === 'string') {
              token = bookingStore.bookingToken;
            }
            const bookingResponse = await fetchBookingData(
              bookingNumber.value,
              false,
              true,
              true,
              token
            )

            if (bookingResponse?.data?.booking) { 
              const modifiedBookingData = {
                ...bookingResponse.data.booking
              };
              if (typeof token === 'string') {
                modifiedBookingData.bookingToken = token;
              }
              bookingStore.setBookingData(modifiedBookingData)
            } else {
              console.error(
                'No booking data returned from API:',
                bookingResponse
              )
              errorStore.setError(t('confirmation.noDetails'))
            }
          } catch (error) {
            console.error('Error fetching booking data:', error)
            errorStore.setError(t('confirmation.fetchError'))
          }
        }
      })

      return {
        t,
        chevron,
        bookingNumber,
        customerEmail,
        outboundTravelInfo,
        returnTravelInfo,
        formatDate,
        formatTime,
      }
    },
  })
</script>

<style lang="scss" scoped>
  @import '@/styles/grid.scss';

  .confirmation-page {
    display: flex;
    flex-direction: column;
    gap: 64px;
    text-align: center;
    max-width: 736px;
    margin-right: auto;
    margin-left: auto;

    &__back-link {
      text-decoration: none;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 2rem 0;
      color: $clr-primary;

      &-icon {
        transform: rotate(270deg);
        width: 16px;
        height: 16px;
      }
    }

    &__main {
      padding: 32px;

      h1 {
        margin-bottom: 32px;
      }
    }

    &__sidebar {
      display: flex;
      flex-direction: column;
      gap: 32px;
      background-color: transparent;
      box-shadow: none;
    }
  }
</style>
